import React, { useMemo, useState } from 'react';
import {
  Row,
  Col,
  Typography,
  Switch,
  Space,
  Segmented,
  Button,
  Input,
  Select,
} from 'antd';
import { SyncOutlined, DesktopOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import type { WorkstationControlCenterQueryParams } from '@/services/workstationControlCenter.service';
import ExecutiveCapacityRibbon from '@/components/manufacturing/workstation-control-center/ExecutiveCapacityRibbon';
import WorkstationStatusBoard from '@/components/manufacturing/workstation-control-center/WorkstationStatusBoard';
import WorkstationCurrentWorkMonitor from '@/components/manufacturing/workstation-control-center/WorkstationCurrentWorkMonitor';
import QueueManagementCenter from '@/components/manufacturing/workstation-control-center/QueueManagementCenter';
import CapacityUtilizationPanel from '@/components/manufacturing/workstation-control-center/CapacityUtilizationPanel';
import HoursAnalysisCenter from '@/components/manufacturing/workstation-control-center/HoursAnalysisCenter';
import FabricationUnitWorkstationView from '@/components/manufacturing/workstation-control-center/FabricationUnitWorkstationView';
import MaterialDependencyPanel from '@/components/manufacturing/workstation-control-center/MaterialDependencyPanel';
import InspectionDependencyPanel from '@/components/manufacturing/workstation-control-center/InspectionDependencyPanel';
import OutsourceDependencyPanel from '@/components/manufacturing/workstation-control-center/OutsourceDependencyPanel';
import WorkstationBottleneckAnalyzer from '@/components/manufacturing/workstation-control-center/WorkstationBottleneckAnalyzer';
import OperatorPerformancePanel from '@/components/manufacturing/workstation-control-center/OperatorPerformancePanel';
import MaintenanceIntegrationPanel from '@/components/manufacturing/workstation-control-center/MaintenanceIntegrationPanel';
import CapacityPlanningView from '@/components/manufacturing/workstation-control-center/CapacityPlanningView';
import WorkstationTimeline from '@/components/manufacturing/workstation-control-center/WorkstationTimeline';
import WorkstationSmartAlertsPanel from '@/components/manufacturing/workstation-control-center/WorkstationSmartAlertsPanel';
import WorkstationAnalytics from '@/components/manufacturing/workstation-control-center/WorkstationAnalytics';

const { Title, Text } = Typography;

const EXECUTION_SCOPES = [
  'Fabrication',
  'Manufacturing',
  'Repair',
  'Repair + Manufacturing',
  'Outsource',
];

const WorkstationControlCenterPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tvMode = searchParams.get('mode') === 'tv';
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [period, setPeriod] = useState<'today' | 'week' | 'month'>('today');
  const [timelinePeriod, setTimelinePeriod] = useState<'today' | 'week' | 'month'>('week');
  const [filters, setFilters] = useState<WorkstationControlCenterQueryParams>({});
  const [lastUpdated, setLastUpdated] = useState(new Date());

  const refreshMs = autoRefresh ? 30000 : undefined;
  const queryParams: WorkstationControlCenterQueryParams = useMemo(
    () => ({ ...filters, period }),
    [filters, period]
  );
  const timelineParams = useMemo(
    () => ({ ...filters, period: timelinePeriod }),
    [filters, timelinePeriod]
  );

  React.useEffect(() => {
    if (!autoRefresh) return;
    const t = setInterval(() => setLastUpdated(new Date()), 30000);
    return () => clearInterval(t);
  }, [autoRefresh]);

  const toggleTv = () => {
    const next = new URLSearchParams(searchParams);
    if (tvMode) next.delete('mode');
    else next.set('mode', 'tv');
    setSearchParams(next);
  };

  return (
    <div
      className={tvMode ? 'workstation-control-center-tv' : ''}
      style={{
        padding: tvMode ? 16 : 24,
        background: '#f0f2f5',
        minHeight: '100vh',
      }}
    >
      <style>{`
        .workstation-control-center-tv .ant-statistic-content { font-size: 26px; }
        .workstation-control-center-tv .ant-card-head-title { font-size: 18px; }
      `}</style>

      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Space align="center">
            <DesktopOutlined style={{ fontSize: tvMode ? 40 : 32, color: '#145A54' }} />
            <div>
              <Title level={tvMode ? 1 : 2} style={{ margin: 0 }}>
                Workstation Control Center
              </Title>
              <Text type="secondary">
                Real-time workload, capacity, queues, and bottlenecks per work center.
              </Text>
            </div>
          </Space>
        </Col>
        <Col>
          <Space wrap>
            <Text type="secondary">Updated {lastUpdated.toLocaleTimeString()}</Text>
            <Segmented
              value={period}
              onChange={(v) => setPeriod(v as 'today' | 'week' | 'month')}
              options={[
                { label: 'Today', value: 'today' },
                { label: 'Week', value: 'week' },
                { label: 'Month', value: 'month' },
              ]}
            />
            <Switch checked={autoRefresh} onChange={setAutoRefresh} checkedChildren={<SyncOutlined spin />} />
            <Button icon={<DesktopOutlined />} type={tvMode ? 'primary' : 'default'} onClick={toggleTv}>
              TV mode
            </Button>
          </Space>
        </Col>
      </Row>

      <Space wrap style={{ marginBottom: 16 }}>
        <Input
          placeholder="Search WO / customer"
          allowClear
          style={{ width: 180 }}
          onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value || undefined }))}
        />
        <Input
          placeholder="Department"
          allowClear
          style={{ width: 140 }}
          onChange={(e) => setFilters((f) => ({ ...f, department: e.target.value || undefined }))}
        />
        <Select
          allowClear
          placeholder="Execution scope"
          style={{ width: 180 }}
          options={EXECUTION_SCOPES.map((s) => ({ label: s, value: s }))}
          onChange={(v) => setFilters((f) => ({ ...f, execution_scope: v }))}
        />
      </Space>

      <ExecutiveCapacityRibbon params={queryParams} refreshInterval={refreshMs} tvMode={tvMode} />

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} xl={16}>
          <WorkstationStatusBoard params={queryParams} />
          <WorkstationCurrentWorkMonitor params={queryParams} />
          <QueueManagementCenter params={queryParams} />
          <CapacityUtilizationPanel params={queryParams} />
          <HoursAnalysisCenter params={queryParams} />
          <FabricationUnitWorkstationView params={queryParams} />
          <MaterialDependencyPanel params={queryParams} />
          <InspectionDependencyPanel params={queryParams} />
          <OutsourceDependencyPanel params={queryParams} />
          <WorkstationBottleneckAnalyzer params={queryParams} />
          <OperatorPerformancePanel params={queryParams} />
          <MaintenanceIntegrationPanel params={queryParams} />
          <CapacityPlanningView params={queryParams} />
          <WorkstationTimeline params={timelineParams} onPeriodChange={setTimelinePeriod} />
          <WorkstationAnalytics params={queryParams} />
        </Col>
        <Col xs={24} xl={8}>
          <WorkstationSmartAlertsPanel params={queryParams} refreshInterval={refreshMs} />
        </Col>
      </Row>
    </div>
  );
};

export default WorkstationControlCenterPage;
